import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Group,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "../db";
import { ThemeConfig, defaultThemeConfig } from "../utils/theme";
import { useTheme } from "../utils/themeContext";
import { IconCheck } from "@tabler/icons-react";

export function ThemeSelector() {
  const mantineTheme = useMantineTheme();
  const { themeConfig, updateTheme } = useTheme();
  const [localThemeConfig, setLocalThemeConfig] = useState<ThemeConfig>(themeConfig);
  const [loading, setLoading] = useState(false);

  // Update local state when themeConfig changes
  useEffect(() => {
    setLocalThemeConfig(themeConfig);
  }, [themeConfig]);

  // Save theme config
  const saveThemeConfig = async (newConfig: ThemeConfig) => {
    setLoading(true);
    try {
      setLocalThemeConfig(newConfig);
      updateTheme(newConfig);
      
      notifications.show({
        title: "Theme Updated",
        message: "Your theme settings have been applied immediately.",
        color: "green",
      });
    } catch (error: any) {
      const message = error.message || "Failed to save theme settings.";
      notifications.show({
        title: "Error",
        color: "red",
        message,
      });
    } finally {
      setLoading(false);
    }
  };

  // Map of Mantine color names to their hex values
  const colorMap: Record<string, string> = {
    "blue": "#228be6",
    "indigo": "#4c6ef5",
    "purple": "#7950f2",
    "pink": "#e64980",
    "red": "#fa5252",
    "orange": "#fd7e14",
    "yellow": "#fab005",
    "lime": "#82c91e",
    "green": "#40c057",
    "teal": "#12b886",
    "cyan": "#15aabf",
    "gray": "#868e96",
    "dark": "#343a40"
  };

  // Handle primary/secondary color change (Mantine color name)
  const handleColorChange = (color: string, property: keyof Pick<ThemeConfig, "primaryColor" | "secondaryColor">) => {
    // Update both the Mantine color name and the custom hex color
    const newConfig = { 
      ...localThemeConfig, 
      [property]: color,
      customColors: {
        ...localThemeConfig.customColors,
        [property === "primaryColor" ? "primary" : "secondary"]: colorMap[color] || "#228be6"
      }
    };
    saveThemeConfig(newConfig);
  };

  // Handle border radius change
  const handleBorderRadiusChange = (value: string | null) => {
    if (!value) return;
    const radius = parseInt(value, 10);
    const newConfig = { ...localThemeConfig, borderRadius: radius };
    saveThemeConfig(newConfig);
  };

  // Reset to default theme
  const handleResetTheme = () => {
    saveThemeConfig(defaultThemeConfig);
  };

  return (
    <Stack spacing="md">
      <Title order={4}>Theme Customization</Title>
      
      <SimpleGrid cols={2} spacing="md" breakpoints={[{ maxWidth: "sm", cols: 1 }]}>
        <Paper p="md" withBorder>
          <Stack>
            <Text weight={500}>Primary Color</Text>
            <Select
              label="Primary Color"
              value={themeConfig.primaryColor}
              onChange={(value) => value && handleColorChange(value, "primaryColor")}
              data={[
                { value: "blue", label: "Blue" },
                { value: "indigo", label: "Indigo" },
                { value: "purple", label: "Purple" },
                { value: "pink", label: "Pink" },
                { value: "red", label: "Red" },
                { value: "orange", label: "Orange" },
                { value: "yellow", label: "Yellow" },
                { value: "lime", label: "Lime" },
                { value: "green", label: "Green" },
                { value: "teal", label: "Teal" },
                { value: "cyan", label: "Cyan" },
                { value: "gray", label: "Gray" },
                { value: "dark", label: "Dark" }
              ]}
              disabled={loading}
            />
            
            <Text size="xs" color="dimmed" mt="md">Preview Color:</Text>
            <Box 
              sx={(theme) => ({
                backgroundColor: colorMap[themeConfig.primaryColor] || "#228be6",
                height: 36,
                borderRadius: theme.radius.sm,
                marginTop: theme.spacing.xs
              })}
            />
          </Stack>
        </Paper>
        
        <Paper p="md" withBorder>
          <Stack>
            <Text weight={500}>Secondary Color</Text>
            <Select
              label="Secondary Color"
              value={themeConfig.secondaryColor}
              onChange={(value) => value && handleColorChange(value, "secondaryColor")}
              data={[
                { value: "red", label: "Red" },
                { value: "pink", label: "Pink" },
                { value: "orange", label: "Orange" },
                { value: "yellow", label: "Yellow" },
                { value: "lime", label: "Lime" },
                { value: "green", label: "Green" },
                { value: "teal", label: "Teal" },
                { value: "cyan", label: "Cyan" },
                { value: "blue", label: "Blue" },
                { value: "indigo", label: "Indigo" },
                { value: "purple", label: "Purple" },
                { value: "gray", label: "Gray" },
                { value: "dark", label: "Dark" }
              ]}
              disabled={loading}
            />
            
            <Text size="xs" color="dimmed" mt="md">Preview Color:</Text>
            <Box 
              sx={(theme) => ({
                backgroundColor: colorMap[themeConfig.secondaryColor] || "#fa5252",
                height: 36,
                borderRadius: theme.radius.sm,
                marginTop: theme.spacing.xs
              })}
            />
          </Stack>
        </Paper>
      </SimpleGrid>
      
      <Paper p="md" withBorder>
        <Stack>
          <Text weight={500}>Border Radius</Text>
          <Select
            value={themeConfig.borderRadius.toString()}
            onChange={handleBorderRadiusChange}
            data={[
              { value: "0", label: "None (0px)" },
              { value: "4", label: "Small (4px)" },
              { value: "8", label: "Medium (8px)" },
              { value: "12", label: "Large (12px)" },
              { value: "16", label: "Extra Large (16px)" },
            ]}
            disabled={loading}
          />
        </Stack>
      </Paper>
      
      <Paper p="md" withBorder>
        <Stack>
          <Text weight={500}>Preview</Text>
          <Box
            sx={(theme) => ({
              padding: "1rem",
              borderRadius: `${themeConfig.borderRadius}px`,
              backgroundColor: themeConfig.customColors.primary,
              color: theme.white,
              textAlign: "center",
            })}
          >
            Primary Color
          </Box>
          
          <Box
            sx={(theme) => ({
              padding: "1rem",
              borderRadius: `${themeConfig.borderRadius}px`,
              backgroundColor: themeConfig.customColors.secondary,
              color: theme.white,
              textAlign: "center",
            })}
          >
            Secondary Color
          </Box>
          
          <Box
            sx={(theme) => ({
              padding: "1rem",
              borderRadius: `${themeConfig.borderRadius}px`,
              border: `1px solid ${themeConfig.customColors.primary}`,
              color: themeConfig.customColors.primary,
              textAlign: "center",
            })}
          >
            Bordered Element
          </Box>
        </Stack>
      </Paper>
      
      <Group position="right">
        <Button
          variant="outline"
          onClick={handleResetTheme}
          disabled={loading}
        >
          Reset to Default
        </Button>
      </Group>
      
      <Text size="sm" color="dimmed">
        Note: All theme changes are applied immediately.
      </Text>
    </Stack>
  );
}
